import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Title, Tooltip } from 'chart.js';
import React from 'react';
import { Bar } from 'react-chartjs-2';
import { Link } from 'react-router-dom';
import { DashBoardDonutCard, IDashboardDonutChartDataSet } from '../../components/UI-Elements/DashBoardDonutCard';
import { StackedBarChartElement } from '../../sp-types/dashboardTypes';
import { FormatToShortPriceTemplate, formatToShortString } from '../../utils/FormatValue';
import {
  MonthYear,
  convertDateToMonthYear,
  getMonthName,
  months,
  sortMonthYear,
} from '../../utils/MonthYear/monthYear';
import { GENERAL_ID } from '../../components/Stores/exposureDataStore';
import { getUnitObj } from '../../utils/MassUnits/massUnitsUtils';
import { MULTIPLE_MATS, NO_DATA, terminalChips, valChips } from './dashboardUtils';
import { ToggleChips } from './dashboard-components/ToggleChips';
import { useMaterialsStore } from '../../components/Stores/MaterialsStore';
import { MonthYearValue } from '../../utils/MonthYear/MonthYearValue';
import { calcMaxVal } from './dashboard-components/DashValuesUtils';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface IStackedBarDoughnutChartCard {
  data: StackedBarChartElement[];
  accumulated: StackedBarChartElement[];
  isFinancialPositions?: boolean;
  cardTitle: string;
  cardDescription: string;
  cardUrl: string;
  materialId: string;
  currentDate?: Date;
  addNewUrl?: string;
  onAddNewClick?: () => void;
  terminals: string;
  isTerminals: boolean;
  setIsTerminals: (_isTerminals: boolean) => void;
  isQuantity?: boolean;
  setIsQuantity: (_isQuan: boolean) => void;
  showDetails?: () => void;
  setMaxVal: (_max: number) => void;
  chartMax: number;
  showProportion: boolean;
}

export const selectBlue = '#6571ff';
const emptyMockFutureOrdersData: StackedBarChartElement[] = [
  {
    materialId: '',
    color: '#EAEAEB',
    fieldName: 'No material',
    valueArray: [],
  },
];

const emptyMockFinancialPositionsData: StackedBarChartElement[] = [
  {
    materialId: '',
    color: '#EAEAEB',
    fieldName: 'No material',
    valueArray: [],
  },
];

function sumValueArrayValues(elements: StackedBarChartElement[]): number {
  let totalSum = 0;

  // Iterate over each StackedBarChartElement
  elements.forEach((element) => {
    // Iterate over each value in the valueArray of the current element
    element.valueArray.forEach((valueObject) => {
      // Add the value to the total sum
      totalSum += valueObject.value;
    });
  });

  return totalSum;
}

export const StackedBarDoughnutChartCard: React.FC<IStackedBarDoughnutChartCard> = ({
  cardTitle,
  data,
  accumulated,
  isFinancialPositions = false,
  cardUrl,
  addNewUrl,
  currentDate = new Date(),
  materialId,
  isTerminals,
  setIsTerminals,
  terminals,
  isQuantity = false,
  setIsQuantity,
  showDetails,
  setMaxVal,
  chartMax,
  showProportion,
  onAddNewClick,
}) => {
  const { materialsMap } = useMaterialsStore();
  const [ratioLine, setRatioLine] = React.useState<string>('');

  React.useEffect(() => {
    setRatioLine(
      cardTitle === 'Financial positions' && materialId !== GENERAL_ID && materialsMap[materialId]?.hasRatio
        ? 'Ratio - 0mt'
        : '',
    );
  }, [materialId, materialsMap]);
  // Function to convert a single StackedBarChartElement to IDashboardDonutChartDataSet
  function convertToDonutChartDataSet(element: StackedBarChartElement, currentDate: Date): IDashboardDonutChartDataSet {
    const currentMonth = currentDate.getMonth() + 1;
    const currentYear = currentDate.getFullYear();
    const value =
      element.valueArray.find((item) => item.date.month === currentMonth && item.date.year === currentYear)?.value || 0;

    return {
      fieldName: element.fieldName,
      color: element.color,
      value: element.sumValue || 1,
      unit: element.unit,
      price: element.price,
      ratio: 0,
    };
  }

  // Function to convert an array of StackedBarChartElement to an array of IDashboardDonutChartDataSet
  function convertArrayToDonutChartDataSetArray(
    elements: StackedBarChartElement[],
    currentDate: Date,
  ): IDashboardDonutChartDataSet[] {
    return elements.map((element) => convertToDonutChartDataSet(element, currentDate));
  }

  const donutChartData = convertArrayToDonutChartDataSetArray(
    isFinancialPositions ? accumulated : data,
    currentDate,
  ).map((item) => ({
    ...item,
    origVal: item.value,
    value: isQuantity && !isFinancialPositions ? item.value * (item.price || 1) : item.value,
  }));
  const materialExposures = donutChartData.filter((a) => !!a.fieldName);

  const unitSum = materialExposures.reduce((sum, item) => sum + item.value / (item.price || 1), 0);
  const secondaryTitle =
    unitSum > 0
      ? materialId === GENERAL_ID
        ? MULTIPLE_MATS
        : `${formatToShortString(unitSum, 1)} ${getUnitObj(materialExposures?.[0].unit).shortForm}`
      : NO_DATA;

  const currentBarData =
    sumValueArrayValues(data) !== 0
      ? data
      : cardTitle == 'Financial positions'
      ? emptyMockFinancialPositionsData
      : emptyMockFutureOrdersData;
  return (
    <div className="flex  flex-col h-full bg-white p-3 overflow-auto ">
      <div className="flex flex-row justify-between">
        <h4 className="text-lg">{cardTitle}</h4>
        <div className="flex flex-row items-center gap-2">
          {(addNewUrl || onAddNewClick) &&
            (onAddNewClick ? (
              <div
                onClick={onAddNewClick}
                className="text-sm text-blue-600 border-1 px-1 rounded border-blue-600 cursor-pointer hover:bg-blue-50"
              >
                <b className="text-base">+</b> Add order
              </div>
            ) : (
              <Link to={addNewUrl!} className="text-sm text-blue-600 border-1 px-1 rounded border-blue-600">
                <b className="text-base">+</b> Add order
              </Link>
            ))}
          <Link to={cardUrl}>
            <img className="" src="/assets/icons/blue-flat/expand.svg" alt="" />
          </Link>
        </div>
      </div>

      <div className="flex flex-col flex-1 justify-between pt-4">
        <div className="flex flex-col flex-1 items-center h-44  ">
          <DashBoardDonutCard
            dataSet={donutChartData}
            secondaryTitle={secondaryTitle}
            ratioLine={ratioLine}
            cssSize="w-52 h-52"
          />
        </div>
        <div className="h-[12.5rem] relative">
          <StackedBarChart
            stackedBarChartData={data}
            terminals={isTerminals ? terminals : ''}
            unit={
              materialId !== GENERAL_ID && isQuantity ? getUnitObj(materialsMap[materialId].unit).shortForm + ' ' : '$'
            }
            isGeneral={materialId === GENERAL_ID}
            setMaxVal={setMaxVal}
            chartMax={chartMax}
            name={cardTitle}
            showProportion={showProportion}
          />
        </div>
        <div className="flex flex-col text-sm items-start text-blue-600">
          {!!terminals && !showDetails && (
            <ToggleChips
              width="5rem"
              selected={isTerminals ? 1 : 0}
              setSelected={(isTerm) => setIsTerminals(isTerm === 1)}
              chips={terminalChips}
            />
          )}
          <div className="flex flex-row-reverse justify-between pt-2 w-full">
            {showDetails && (
              <div
                className="flex scale-90 gap-2 border-1 px-1 py-0.5 rounded border-blue-600 cursor-pointer hover:bg-gray-75 transform hover:scale-100 transition-transform duration-300"
                onClick={showDetails}
              >
                Show details
              </div>
            )}
            {materialId !== GENERAL_ID && (
              <ToggleChips
                width="5rem"
                selected={isQuantity ? 1 : 0}
                setSelected={(isQuan) => setIsQuantity(isQuan === 1)}
                chips={valChips}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

type StackedBarChartProps = {
  stackedBarChartData: StackedBarChartElement[];
  xAxisTitle?: string;
  terminals: string;
  firstMonthName?: string;
  isCumulative?: boolean;
  isGeneral?: boolean;
  unit?: string;
  setMaxVal: (_max: number) => void;
  chartMax: number;
  name: string;
  showProportion: boolean;
};

export const StackedBarChart: React.FC<StackedBarChartProps> = ({
  stackedBarChartData,
  xAxisTitle,
  terminals,
  firstMonthName,
  isCumulative = false,
  isGeneral = false,
  unit = '$',
  setMaxVal,
  chartMax,
  showProportion,
  name,
}) => {
  const { year, month: origMonth } = convertDateToMonthYear(new Date());

  const origFirstMon = getMonthName({ year, month: origMonth === 1 ? 12 : origMonth - 1 });

  const labels = [
    ...Array.from(
      new Set(
        stackedBarChartData
          .reduce((acc, itm) => [...acc, ...itm.valueArray.map(({ date }) => date)], [] as MonthYear[])
          .sort(sortMonthYear)
          .filter(
            (a, i) =>
              !terminals || !terminals.trim() || terminals.includes(`${a.month - 1}`) || (firstMonthName && i === 0),
          )
          .map(getMonthName),
      ),
    ),
    ...(firstMonthName ? [origFirstMon] : []),
  ];
  console.log('labels', name, labels);
  console.log('stackedBarChartData', name, stackedBarChartData);
  React.useEffect(() => {
    if (stackedBarChartData && showProportion) {
      const max = calcMaxVal(stackedBarChartData, isGeneral);
      setMaxVal(max);
    }
  }, [stackedBarChartData, isGeneral, showProportion]);

  const [firstMonth, ...otherMonths] = labels;

  const finalLabels = firstMonthName
    ? [firstMonthName, ...otherMonths, ...(terminals.includes(`${origMonth - 2}`) ? [origFirstMon] : [])]
    : labels.length > 0
    ? labels
    : months;

  const getTerminalValue = (valueArray: MonthYearValue[], termName: string): { value: number } => {
    const terminalIndex = months.indexOf(termName);
    const monthsOfTerm: number[] = [terminalIndex];
    for (let i = 1; i < 4; i++) {
      const prevMonth = (12 + terminalIndex - i) % 12;
      if (terminals.includes(prevMonth.toString())) {
        break;
      }
      monthsOfTerm.push(prevMonth);
    }

    const value =
      valueArray.filter(({ date }) => monthsOfTerm.includes(date.month - 1))?.reduce((a, { value }) => a + value, 0) ||
      0;
    return { value };
  };

  const now = new Date();
  const datasets = stackedBarChartData
    .filter((a) => !!a.fieldName)
    .map((item) => ({
      label: item.fieldName,
      data: labels.map((shortLabel, monthInd) => {
        const dataPoint =
          terminals && !isCumulative
            ? getTerminalValue(item.valueArray, shortLabel)
            : item.valueArray.find(
                (value) =>
                  getMonthName(value.date) === shortLabel && (monthInd < 12 || value.date.year > now.getFullYear()),
              );

        if (name === 'inventory' && monthInd === 0) {
          console.log('First bar (storage):', {
            shortLabel,
            monthInd,
            dataPoint,
            valueArray: item.valueArray.slice(0, 3),
          });
        }

        return dataPoint ? dataPoint.value.toFixed(0) : 0;
      }),
      backgroundColor: (ctx: any) => (!isGeneral && firstMonthName && ctx.index === 0 ? selectBlue : item.color),
      stack: 'combined',
    }));

  const data = { labels: finalLabels, datasets };

  const options = {
    responsive: true,
    barPercentage: 1,
    categoryPercentage: 1,
    maxBarThickness: 15,
    minBarThickness: 10,
    maintainAspectRatio: false,

    plugins: {
      legend: {
        display: false,
      },
    },

    scales: {
      x: {
        title: {
          display: true,
          text: xAxisTitle ? xAxisTitle : 'Month',
        },
        ticks: {
          color: (context: any) => (firstMonthName && context.tick.value === 0 ? selectBlue : 'rgba(0, 0, 0, 0.5)'),
        },

        grid: {
          drawOnChartArea: false,
          drawTicks: false,
        },
      },

      y: {
        ...(showProportion && chartMax > 0 ? { min: 0, max: chartMax } : {}),
        title: {
          display: false,
          text: 'USD in millions',
        },
        stacked: true,
        ticks: {
          count: 5,
          callback: function (value: any) {
            return FormatToShortPriceTemplate(value, unit, 0);
          },
        },
        grid: {
          color: (context: any) => {
            const value = context.tick.value as number;
            if (value === 0) {
              return '#AFB0B0'; // Change grid color to red for values greater than 5
            }
            return 'rgba(0, 0, 0, 0.1)'; // Default grid color
          },
          drawOnChartArea: true,
          drawTicks: true, // Disable grid lines on the tick marks
        },
      },
    },
  };

  return <Bar className="" data={data} options={options} />;
};
